'use client';

import React, { ReactNode } from 'react';
import SetupYourAccount from './SetupYourAccount';
import tokens from '@verifime/design-tokens';
import SetupAuthenticatorApp, { TSetupAuthenticatorAppProps } from './SetupAuthenticatorApp';
import { ContainerLoading } from './Loading';
import { SetupState } from '../../types';
import SetupPasskey, { TSetupPasskeyProps } from './SetupPasskey';
import { LoginCenterContainer, LoginContainer } from '../common/LoginContainer';
import { Credential } from '@verifime/utils';

export type TCredentialSetupProps = {
  hasRegisteredFIDO: () => Promise<boolean>;
  hasRegisteredOTP: () => Promise<boolean>;
  noCredentialComponent?: ({
    otpUserDisplayName,
    goNext,
  }: {
    otpUserDisplayName: string;
    goNext: () => void;
  }) => ReactNode;
  hidePasskeySetupOption: boolean;
} & TSetupPasskeyProps &
  TSetupAuthenticatorAppProps;

export type TInitialCredentialSetupProps = Omit<TCredentialSetupProps, 'setSetupState'>;

export default function InitialCredentialSetup({
  hasRegisteredFIDO,
  hasRegisteredOTP,
  noCredentialComponent,
  hidePasskeySetupOption,
  ...props
}: TInitialCredentialSetupProps) {
  const { setupComplete, otpUserDisplayName } = props;
  const [setupState, setSetupState] = React.useState<SetupState>(SetupState.LOADING);

  React.useEffect(() => {
    setSetupState(SetupState.LOADING);
    Promise.all([hasRegisteredFIDO(), hasRegisteredOTP()]).then(
      ([fidoRegistered, otpRegistered]) => {
        if (fidoRegistered || otpRegistered) {
          setupComplete(Credential.HasCredential);
          return;
        }

        if (typeof noCredentialComponent === 'function') {
          setSetupState(SetupState.NO_CREDENTIAL);
          return;
        }

        setSetupState(hidePasskeySetupOption ? SetupState.TOTP : SetupState.FIDO_STEP1);
      },
    );
  }, []);

  return (
    <>
      {setupState === SetupState.LOADING && <ContainerLoading />}
      {setupState === SetupState.NO_CREDENTIAL &&
        noCredentialComponent({
          otpUserDisplayName,
          goNext: () => {
            setSetupState(hidePasskeySetupOption ? SetupState.TOTP : SetupState.FIDO_STEP1);
          },
        })}
      {setupState === SetupState.FIDO_STEP1 && (
        <SetupYourAccount setSetupState={setSetupState} otpUserDisplayName={otpUserDisplayName} />
      )}
      {setupState === SetupState.FIDO_STEP2 && (
        <SetupPasskey setSetupState={setSetupState} {...props} />
      )}
      {setupState === SetupState.TOTP && (
        <LoginContainer sx={{ px: tokens.spacingBase }}>
          <LoginCenterContainer gap={tokens.spacingSm}>
            <SetupAuthenticatorApp
              setSetupState={setSetupState}
              otpUserDisplayName={otpUserDisplayName}
              displayBackOnEntry={!hidePasskeySetupOption}
              {...props}
            />
          </LoginCenterContainer>
        </LoginContainer>
      )}
    </>
  );
}
